// The measurement block a 12-lead machine prints (ventricular rate, PR interval, QRS duration, QT and QTc, P-R-T axes), read
// from the same parameters the trace is drawn with, so the numbers always match the waves. A value the rhythm does not have, or
// that the synthesiser does not model, is left undefined and printed as ---; nothing is estimated. There is never an
// interpretation: reading the ECG is the question.
import type { EcgParams } from './types';
import { BROAD_QRS_MS, broadNet, broadQrsMs, leadMorph, profileFor, QT_RANGE, RR_FOR_QT } from './synth';

export { BROAD_QRS_MS };

export interface Measurements {
  /** Ventricular rate, per minute. */
  rate?: number;
  /** PR interval, ms. */
  pr?: number;
  /** QRS duration, ms. */
  qrs?: number;
  /** QT and QTc (Bazett, QT / √RR), ms. */
  qt?: number;
  qtc?: number;
  /** Frontal-plane axes in degrees, in (−180, 180]. */
  pAxis?: number;
  qrsAxis?: number;
  tAxis?: number;
}

/** What the header needs: the rhythm and rate, and any of the synthesiser's other parameters the trace is drawn with. */
export type MeasureInput = Pick<EcgParams, 'rate' | 'rhythm'> & Partial<Omit<EcgParams, 'rate' | 'rhythm'>>;

/** The synthesiser's defaults for a trace that names none (synth.ts leadMorph). */
const PR_MS = 160;
const QRS_MS = 90;
const QTC_MS = 420;
const QRS_AXIS = 60;

const clamp = (x: number, lo: number, hi: number) => (x < lo ? lo : x > hi ? hi : x);

/** An angle in whole degrees, in (−180, 180]. */
function degrees(a: number): number {
  let d = Math.round(a);
  while (d <= -180) d += 360;
  while (d > 180) d -= 360;
  return d;
}

/**
 * A frontal-plane axis from the net deflections in lead I (0°) and aVF (+90°), the textbook quick method; undefined when both
 * are close to flat, where any angle would be a guess.
 */
export function axisFrom(leadI: number, leadAvf: number): number | undefined {
  if (Math.hypot(leadI, leadAvf) < 0.02) return undefined;
  return degrees((Math.atan2(leadAvf, leadI) * 180) / Math.PI);
}

/** The measurement block in words, for the sheet's spoken description: what is printed, and nothing about the rhythm. */
export function spokenMeasurements(m: Measurements): string {
  const said: string[] = [];
  if (m.rate !== undefined) said.push(`Ventricular rate ${m.rate} per minute.`);
  if (m.pr !== undefined) said.push(`PR interval ${m.pr} milliseconds.`);
  if (m.qrs !== undefined) said.push(`QRS duration ${m.qrs} milliseconds.`);
  if (m.qt !== undefined && m.qtc !== undefined) said.push(`QT ${m.qt}, QTc ${m.qtc} milliseconds.`);
  const axes = ([['P', m.pAxis], ['QRS', m.qrsAxis], ['T', m.tAxis]] as const).filter(([, v]) => v !== undefined).map(([name, v]) => `${name} ${v}`);
  if (axes.length) said.push(`Axes: ${axes.join(', ')} degrees.`);
  return said.length ? said.join(' ') : 'No measurements printed.';
}

export function measure(input: MeasureInput): Measurements {
  const params: EcgParams = { ...input, stShift: input.stShift ?? {}, machineRead: input.machineRead ?? [] };
  const prof = profileFor(params);
  if (!prof.beats) return {}; // VF, asystole: nothing a machine can measure
  const I = leadMorph(params, 'I');
  const aVF = leadMorph(params, 'aVF');
  const out: Measurements = { rate: Math.round(prof.rate) };

  if (prof.kind === 'vt' || prof.kind === 'pea') {
    // The broad complex (synth.ts broadMorph): no P wave, two halves projected from the ventricular vectors and a discordant T.
    // The axes are read from the net deflection in I and aVF as drawn (each half's area: its height times its width); its T is
    // not drawn from the QTc and, at VT rates, runs into the next complex, so there is no QT to print.
    out.qrs = broadQrsMs(params);
    out.qrsAxis = axisFrom(broadNet(I), broadNet(aVF));
    out.tAxis = axisFrom(I.bT, aVF.bT);
    return out;
  }

  if (prof.kind === 'sinus') {
    out.pr = Math.round(params.prMs ?? PR_MS);
    out.pAxis = axisFrom(I.P, aVF.P);
  }
  out.qrs = Math.round(params.qrsMs ?? QRS_MS);
  // The QT the T waves are drawn at, exactly as synth.ts leadMorph computes it; the QTc printed is Bazett's from that QT and the
  // true mean RR, so it moves off the asked QTc only where the synthesiser clamps the RR (rates under about 38 or over 200) or
  // the QT (QT_RANGE, 0.22 to 0.68 s); the API never serves a QTc outside those (itemize.ts ecgMeasuresProblem).
  const rr = clamp(prof.rr0, RR_FOR_QT[0], RR_FOR_QT[1]);
  const qt = clamp(((params.qtcMs ?? QTC_MS) / 1000) * Math.sqrt(rr), QT_RANGE[0], QT_RANGE[1]);
  out.qt = Math.round(qt * 1000);
  out.qtc = Math.round((qt / Math.sqrt(prof.rr0)) * 1000);
  // The limb leads are drawn at the asked axis (the default table is the synthesiser's +60°); the T waves as drawn, after any
  // axis, inversion or ST change, give the T axis.
  out.qrsAxis = degrees(params.axisDeg ?? QRS_AXIS);
  out.tAxis = axisFrom(I.T, aVF.T);
  return out;
}
